import SaveAltIcon from '@mui/icons-material/SaveAlt'
import { useEffect, useState } from 'react'
import {
    SaveButton,
    Toolbar,
    useDataProvider,
    useNotify,
    useRecordContext,
    useRedirect,
    useRefresh,
    useResourceContext,
    useTranslate
} from 'react-admin'

import { useCreateRetailerRetailerTagMutation } from 'apollo/retailer_manager/mutations/CreateRetailerRetailerTag.generated'
import { useDeleteRetailerRetailerTagMutation } from 'apollo/retailer_manager/mutations/DeleteRetailerRetailerTag.generated'
import { useRetailersRetailerQuery } from 'apollo/retailer_manager/queries/RetailersRetailer.generated'

import { useParams } from 'react-router'
import { ChoiceType } from 'types'

const RetailerEditToolbar = (props) => {
    const translate = useTranslate()
    const redirect = useRedirect()
    const refresh = useRefresh()
    const notify = useNotify()
    const resource = useResourceContext()
    const dataProvider = useDataProvider()
    const [createRetailerRetailerTag] = useCreateRetailerRetailerTagMutation()
    const [deleteRetailerRetailerTag] = useDeleteRetailerRetailerTagMutation()
    const [initialTags, setInitialTags] = useState<ChoiceType[]>([])
    const record = useRecordContext()
    const params = useParams()

    // Retreive all tags related to this retailerId
    const { data: retailerTags } = useRetailersRetailerQuery({
        variables: {
            retailerId: (record || { id: '' }).id.toString()
        }
    })

    // Format and set values
    useEffect(() => {
        if (retailerTags?.retailersRetailer?.tags) {
            setInitialTags(
                retailerTags.retailersRetailer.tags.map(
                    (item): ChoiceType => ({
                        id: item.id,
                        name: item.tag
                    })
                )
            )
        }
    }, [retailerTags])

    // Custom save so that we can mutate the tags seperately
    const handleSave = (values, redirectAfterSave) => {
        const retailerId = params.id
        if (!resource || !retailerId || !record) return
        const currentTags = values.tags

        // DELETED values
        const deletedDiff =
            initialTags &&
            initialTags.filter((a) => {
                return !currentTags.find((b) => a.id === b.id)
            })

        // ADDED values
        const addedDiff =
            currentTags &&
            currentTags.filter((a) => {
                return !initialTags.find((b) => a.id === b.id)
            })

        if (deletedDiff) {
            // Delete relation for removed tags
            deletedDiff.forEach((tag) => {
                deleteRetailerRetailerTag({
                    variables: {
                        retailerId: parseInt(retailerId),
                        retailerTagId: parseInt(tag.id)
                    }
                })
            })
        }

        if (addedDiff) {
            // Add relation for new tags
            addedDiff.forEach((tag) => {
                createRetailerRetailerTag({
                    variables: {
                        retailerId: parseInt(retailerId),
                        retailerTagId: parseInt(tag.id)
                    }
                })
            })
        }

        // Update retailer
        dataProvider
            .update(resource, {
                id: retailerId,
                data: record,
                previousData: record
            })
            .then(() => {
                if (redirectAfterSave) {
                    redirect(props.basePath)
                    refresh()
                }
            })
            .finally(() =>
                notify(`${resource} ${translate('manager.resources.general.edited_successfully')}`)
            )
    }

    return (
        <Toolbar
            {...props}
            sx={{
                marginTop: 0
            }}
        >
            <SaveButton
                type="button"
                alwaysEnable={true}
                sx={{
                    marginRight: '1rem'
                }}
                label={translate('manager.resources.general.save')}
                onClick={(values) => {
                    handleSave(values, false)
                }}
            />
            <SaveButton
                label={translate('manager.resources.general.save_and_go_back')}
                color="secondary"
                icon={<SaveAltIcon />}
                onClick={(values) => {
                    handleSave(values, true)
                    redirect('list', resource)
                }}
            />
        </Toolbar>
    )
}

export default RetailerEditToolbar
